import {
  BILLING_ADMISSION_MESSAGES,
  billingErrorReason,
} from '@shared/utils/billingAdmissionError';
export { billingErrorReason } from '@shared/utils/billingAdmissionError';

const messages: Record<string, string> = {
  ...BILLING_ADMISSION_MESSAGES,
  auto_top_up_settings_invalid:
    'Choose a threshold from 1 to 500 credits and a budget from USD 75 to 300.',
  auto_top_up_settings_stale:
    'Automatic top-up settings changed. Reopen settings and review the current limits.',
  auto_top_up_budget_already_used:
    'The budget cannot be below automatic spending already confirmed or pending in this paid period.',
  auto_top_up_purchase_pending:
    'Resolve the pending automatic purchase before changing its limits.',
  auto_top_up_unavailable:
    'Automatic top-up is unavailable for the current paid period or package.',
  auto_top_up_consent_required: 'Review and accept the automatic payment terms first.',
  auto_top_up_setup_unknown:
    'Card setup could not be confirmed. Continue setup to recover the existing session.',
  auto_top_up_setup_expired: 'Card setup expired. Start again to save your card.',
  auto_top_up_setup_invalid:
    'The saved card could not be verified for this company. Start a fresh card setup.',
  auto_top_up_setup_incomplete:
    'Complete card setup in Stripe before enabling automatic purchases.',
  auto_top_up_setup_stale: 'These settings changed while card setup was open. Start a fresh setup.',
  auto_top_up_destination_invalid:
    'A secure payment page could not be verified. Reload the page or contact a billing operator.',
  auto_top_up_outcome_unknown:
    'This purchase is still being reconciled. Automatic purchases are off if you requested cancellation; reload the page before trying again.',
  auto_top_up_cancel_pending:
    'Automatic purchases are off. Stripe has not confirmed cancellation of this purchase yet; reload the page or contact a billing operator.',
  auto_top_up_purchase_invalid: 'This automatic purchase is unavailable for the current company.',
  auto_top_up_invoice_invalid: 'This automatic purchase needs billing operator attention.',

  top_up_checkout_paused: 'New top-up purchases are currently paused.',
  top_up_not_available: 'This package is unavailable. Reload the page and check your paid period.',
  top_up_paid_period_required: 'An active paid period is required to purchase credits.',
  top_up_in_progress:
    'Another top-up purchase is pending. Continue it or reload the page to check its payment status.',
  top_up_outcome_unknown:
    'The checkout response is unconfirmed. Reload the page to recover the existing payment before trying again.',
  top_up_price_invalid: 'This package needs a billing configuration review.',
  top_up_identity_invalid: 'This purchase needs billing operator attention.',
  top_up_payment_invalid: 'Payment evidence needs billing operator attention.',
  top_up_session_invalid:
    'Checkout could not be verified. Reload the page or contact a billing operator.',

  billing_downgrade_incompatible: 'This plan is not a compatible lower monthly plan.',
  billing_upgrade_payment_unavailable:
    'The pending payment could not be verified. Reload this page or contact a billing operator.',
  stripe_schedule_not_owned: 'This subscription schedule needs billing operator attention.',
  stripe_scheduled_change_requires_review:
    'The scheduled change needs reconciliation before another change.',
  enrollment_paused: 'New company enrollment is paused.',
  company_not_eligible: 'This company is outside the approved unused-company enrollment cohort.',
  billing_policy_invalid: 'Select an existing approved enrollment policy.',
  billing_busy: 'Another billing operation is in progress. Refresh and try again.',
  billing_payment_exception_already_resolved:
    'This case has already been reviewed. Refresh payment reviews.',
  billing_payment_exception_not_found:
    'This payment review is no longer available for this company. Refresh payment reviews.',
  billing_change_pending:
    'A billing change is already pending. Reload this page to check its status first.',
  billing_upgrade_preview_stale:
    'The preview has expired or the subscription changed. Request a fresh preview.',
  billing_upgrade_incompatible: 'This plan change needs a different billing flow.',
  billing_upgrade_state_requires_sync:
    'Reload this page to refresh billing before requesting this change.',
  billing_plan_changes_paused: 'Plan changes are currently paused.',
  stripe_not_configured: 'Payment configuration is not available yet.',
  stripe_retry_required:
    'Payment status could not be confirmed. Reload this page before trying again.',
  stripe_invoice_not_paid: 'Payment is not yet confirmed.',
  stripe_upgrade_effective_pending:
    'The payment or effective subscription change is still pending.',
  stripe_upgrade_outcome_unknown:
    'The previous request has an unconfirmed outcome. Reload this page or contact a billing operator.',
};

export const billingErrorMessage = (error: unknown): string => {
  if (error && typeof error === 'object' && 'status' in error && error.status === 403)
    return 'Billing permission has changed. Refresh your account access.';
  const reason = billingErrorReason(error);
  return reason && Object.prototype.hasOwnProperty.call(messages, reason)
    ? messages[reason]
    : 'The billing request could not be completed. Refresh and try again.';
};

/** Only origins returned by the supported hosted payment flows may take over this tab. */
export const hostedBillingUrl = (
  value: string,
  kind: 'checkout' | 'portal' | 'invoice',
): string => {
  const url = new URL(value);
  if (
    url.protocol !== 'https:' ||
    url.username ||
    url.password ||
    url.port ||
    url.hostname !==
      {
        checkout: 'checkout.stripe.com',
        portal: 'billing.stripe.com',
        invoice: 'invoice.stripe.com',
      }[kind]
  )
    throw new Error('Unsupported billing destination');
  return url.href;
};
